import { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Menu, X, Info, Crown, Home, FileText, Building2, MessageSquare, User, HelpCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Profile from './Profile';
// Lazy load modals
const DashboardInfoModal = lazy(() => import('./modals/DashboardInfoModal'));
const AboutModal = lazy(() => import('./modals/AboutModal'));
const PrivacySecurityModal = lazy(() => import('./modals/PrivacySecurityModal'));
const ProfileModal = lazy(() => import('./modals/ProfileModal'));
const SettingsModal = lazy(() => import('./modals/SettingsModal'));
const UserManagementModal = lazy(() => import('./modals/UserManagementModal'));

interface PremiumNavbarProps {
  onSignOut?: () => void;
  onMenuToggle?: () => void;
  isMobileMenuOpen?: boolean;
}

export default function PremiumNavbar({ onSignOut, onMenuToggle, isMobileMenuOpen }: PremiumNavbarProps) {
  const { user } = useAuth();
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isUserManagementModalOpen, setIsUserManagementModalOpen] = useState(false);

  const getSubscriptionBadge = () => {
    if (!user) return null;
    
    if (user.is_premium) {
      return (
        <div className="flex items-center space-x-1 px-2.5 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-md text-xs font-semibold shadow-sm">
          <Crown className="w-3 h-3" />
          <span className="hidden sm:inline">Premium</span>
        </div>
      );
    }
    
    return (
      <div className="px-2.5 py-1.5 bg-gray-100 text-gray-700 rounded-md text-xs font-semibold border border-gray-200">
        <span className="hidden sm:inline">Free</span>
        <span className="sm:hidden">F</span>
      </div>
    );
  };

  return (
    <div className="w-full flex items-center justify-between px-6 lg:px-8" style={{ height: '72px' }}>
      {/* Left Side - Mobile Menu */}
      <div className="flex items-center">
        {/* Mobile Menu Button */}
        <button
          onClick={onMenuToggle}
          className="lg:hidden flex items-center justify-center w-14 h-14 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Toggle Sidebar"
        >
          {isMobileMenuOpen ? (
            <X className="w-7 h-7" />
          ) : (
            <Menu className="w-7 h-7" />
          )}
        </button>
      </div>

      {/* Right Side - Help, About, Profile */}
      <div className="flex items-center gap-3">
        {/* Help & About Buttons */}
        <div className="hidden sm:flex items-center gap-3">
          <button
            onClick={() => setIsHelpModalOpen(true)}
            className="flex items-center justify-center w-14 h-14 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Help & Support"
          >
            <HelpCircle className="w-7 h-7" />
          </button>
          <button
            onClick={() => setIsAboutModalOpen(true)}
            className="flex items-center justify-center w-14 h-14 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="About"
          >
            <Info className="w-7 h-7" />
          </button>
        </div>

        {/* Subscription Badge - Hidden */}
        {/* <div className="hidden sm:block">
          {getSubscriptionBadge()}
        </div> */}
        
        {/* Profile Component */}
        <div className="flex-shrink-0">
          <Profile
            onSignOut={onSignOut}
            onMyProfileClick={() => setIsProfileModalOpen(true)}
            onSettingsClick={() => setIsSettingsModalOpen(true)}
            onUserManagementClick={() => setIsUserManagementModalOpen(true)}
            onProfileClick={() => setIsProfileModalOpen(true)}
          />
        </div>
      </div>

      {/* Modals */}
      <Suspense fallback={null}>
        {isHelpModalOpen && (
          <DashboardInfoModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
        )}
        {isAboutModalOpen && (
          <AboutModal isOpen={isAboutModalOpen} onClose={() => setIsAboutModalOpen(false)} />
        )}
        {isPrivacyModalOpen && (
          <PrivacySecurityModal onClose={() => setIsPrivacyModalOpen(false)} />
        )}
        {isProfileModalOpen && (
          <ProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} />
        )}
        {isSettingsModalOpen && (
          <SettingsModal isOpen={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} />
        )}
        {isUserManagementModalOpen && (
          <UserManagementModal isOpen={isUserManagementModalOpen} onClose={() => setIsUserManagementModalOpen(false)} />
        )}
      </Suspense>
    </div>
  );
}
